// src/components/household-contents/document/DocumentViewer/DocumentViewer.tsx

import { ArrowDownTrayIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { Suspense, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { Document } from "@/api/rest/models/document";
import { downloadDocument2, getDocumentUrl2 } from "@/api/rest/services/documentService";
import Button from "@/components/common/Button";
import DocumentPreviewLoader from "@/components/household-contents/document/DocumentsPreviewLoader";

import DocumentError from "./DocumentError";
import { getDocumentViewer } from "./viewers";

interface DocumentViewerProps {
  document: Document;
  className?: string;
  buildingId?: number;
  householdId?: number;
}

export const DocumentViewer: React.FC<DocumentViewerProps> = ({ document, className = "h-full" }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isStillLoading, setIsStillLoading] = useState(false);
  const [error, setError] = useState(false);

  // Stable callbacks: viewers restart their load timers when these change.
  const handleSuccess = useCallback(() => {
    setIsLoading(false);
    setError(false);
  }, []);

  const handleError = useCallback(() => {
    setIsLoading(false);
    setError(true);
  }, []);

  const handleStillLoading = useCallback(() => setIsStillLoading(true), []);

  const handleDownload = () => {
    downloadDocument2(getDocumentUrl2(document), document.originalFilename);
  };

  const viewerClassName = `${className} ${isLoading ? "hidden" : "block"}`;

  // Get the appropriate viewer component based on document type
  const Viewer = getDocumentViewer(document);

  return (
    <div className="relative h-full">
      {isLoading && (
        <div className="absolute inset-0 flex justify-center items-center">
          <DocumentPreviewLoader />
          {isStillLoading && (
            <div className="absolute inset-x-0 top-1/2 mt-20 flex flex-col items-center gap-3 px-4 text-center">
              <p className="text-gray-600">{t("files.stillLoading")}</p>
              <div className="flex flex-wrap justify-center gap-2">
                <a
                  href={getDocumentUrl2(document)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                >
                  <ArrowTopRightOnSquareIcon className="w-4 h-4 mr-2" />
                  {t("files.openInNewTab")}
                </a>
                <Button variant="outline" onClick={handleDownload}>
                  <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                  {t("files.download")}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {error && !isLoading && (
        <div className="absolute inset-0">
          <DocumentError onDownload={handleDownload} />
        </div>
      )}

      {!error && Viewer && (
        <Suspense fallback={<DocumentPreviewLoader />}>
          <Viewer
            document={document}
            onSuccess={handleSuccess}
            onError={handleError}
            onStillLoading={handleStillLoading}
            className={viewerClassName}
          />
        </Suspense>
      )}
    </div>
  );
};
